import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAdminFeatureFlags } from "@/hooks/use-admin-feature-flags";
import { RefreshCw, ChevronDown, Plus, X, RotateCcw } from "lucide-react";

export default function AdminFeatureFlags() {
  const { flags, loading, fetchFlags, updateFlag, addToAllowlist, removeFromAllowlist, resetToSafeDefault } = useAdminFeatureFlags();
  const [expandedFlags, setExpandedFlags] = useState<Set<string>>(new Set());
  const [newAllowlistUserId, setNewAllowlistUserId] = useState<Record<string, string>>({});
  const [resetConfirm, setResetConfirm] = useState<string | null>(null);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const toggleExpanded = (key: string) => {
    const next = new Set(expandedFlags);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setExpandedFlags(next);
  };

  const handleToggle = async (key: string, currentValue: boolean) => {
    const success = await updateFlag(key, !currentValue);
    if (success) {
      fetchFlags();
    }
  };

  const handleRolloutChange = async (key: string, value: number[]) => {
    const success = await updateFlag(key, undefined, value[0]);
    if (success) {
      fetchFlags();
    }
  };

  const handleAddToAllowlist = async (key: string) => {
    const userId = newAllowlistUserId[key];
    if (!userId) return;
    
    const success = await addToAllowlist(key, userId);
    if (success) {
      setNewAllowlistUserId((prev) => ({ ...prev, [key]: "" }));
      fetchFlags();
    }
  };

  const handleRemoveFromAllowlist = async (key: string, userId: string) => {
    const success = await removeFromAllowlist(key, userId);
    if (success) {
      fetchFlags();
    }
  };

  const handleReset = async () => {
    if (!resetConfirm) return;
    const success = await resetToSafeDefault(resetConfirm);
    if (success) {
      fetchFlags();
    }
    setResetConfirm(null);
  };

  // Group flags by category
  const groupedFlags = flags.reduce((acc, flag) => {
    const category = flag.category || "general";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(flag);
    return acc;
  }, {} as Record<string, typeof flags>);

  return (
    <AdminLayout 
      title="Feature Flags" 
      description="Control feature rollouts and access"
    >
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-muted-foreground">
          {flags.length} feature flags configured
        </div>
        <Button variant="outline" onClick={fetchFlags} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading && flags.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">Loading feature flags...</div>
      ) : flags.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No feature flags configured
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedFlags).map(([category, categoryFlags]) => (
            <div key={category}>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                {category}
              </h3>
              <div className="space-y-3">
                {categoryFlags.map((flag) => (
                  <Card key={flag.key}>
                    <Collapsible open={expandedFlags.has(flag.key)}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={flag.enabled ?? false}
                              onCheckedChange={() => handleToggle(flag.key, flag.enabled ?? false)}
                            />
                            <div>
                              <CardTitle className="text-base">{flag.key}</CardTitle>
                              {flag.description && (
                                <CardDescription className="mt-1">
                                  {flag.description}
                                </CardDescription>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {flag.rollout_percent ?? 0}% rollout
                            </Badge>
                            {flag.requires_confirmation && (
                              <Badge variant="destructive">Requires Confirmation</Badge>
                            )}
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => toggleExpanded(flag.key)}>
                                <ChevronDown 
                                  className={`h-4 w-4 transition-transform ${
                                    expandedFlags.has(flag.key) ? "rotate-180" : ""
                                  }`} 
                                />
                              </Button>
                            </CollapsibleTrigger>
                          </div>
                        </div>
                      </CardHeader>

                      <CollapsibleContent>
                        <CardContent className="pt-0 space-y-4">
                          {/* Rollout Percentage */}
                          <div>
                            <label className="text-sm font-medium mb-2 block">
                              Rollout Percentage: {flag.rollout_percent ?? 0}%
                            </label>
                            <Slider
                              value={[flag.rollout_percent ?? 0]}
                              min={0}
                              max={100}
                              step={5}
                              onValueCommit={(value) => handleRolloutChange(flag.key, value)}
                              className="w-full"
                            />
                          </div>

                          {/* Allowlist */}
                          <div>
                            <label className="text-sm font-medium mb-2 block">
                              Allowlist ({(flag.allowlist_user_ids || []).length} users)
                            </label>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {(flag.allowlist_user_ids || []).map((userId) => (
                                <Badge 
                                  key={userId} 
                                  variant="secondary"
                                  className="flex items-center gap-1"
                                >
                                  {userId.slice(0, 8)}...
                                  <button
                                    onClick={() => handleRemoveFromAllowlist(flag.key, userId)}
                                    className="ml-1 hover:text-destructive"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                placeholder="User ID to add..."
                                value={newAllowlistUserId[flag.key] || ""}
                                onChange={(e) => setNewAllowlistUserId((prev) => ({ 
                                  ...prev, 
                                  [flag.key]: e.target.value 
                                }))}
                                className="flex-1"
                              />
                              <Button 
                                size="sm"
                                onClick={() => handleAddToAllowlist(flag.key)}
                                disabled={!newAllowlistUserId[flag.key]}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add
                              </Button>
                            </div>
                          </div>

                          {/* Emergency Reset */}
                          <div className="pt-2 border-t">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-orange-500 hover:text-orange-600"
                              onClick={() => setResetConfirm(flag.key)}
                            >
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Reset to Safe Default ({flag.safe_default ? "enabled" : "disabled"})
                            </Button>
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={!!resetConfirm} onOpenChange={() => setResetConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Feature Flag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset "{resetConfirm}" to its safe default state? 
              This will also set the rollout percentage to 0%.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} className="bg-orange-500 hover:bg-orange-600">
              Reset to Safe Default
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
